// stores/rsr.store.ts
import { defineStore } from "pinia"

/* =========================
   Types
========================= */

export type RsrResultRow = {
  symbol: string
  rsr_low: number
  rsr_close: number
  s1_low: number
  s2_low: number
  s2_close: number
  value_end: number
  avg_value_5d: number
  has_large_tr_5d: boolean
  pct_chg: number
}

export type RsrApiResponse = {
  meta: {
    start_date: string
    end_date: string
    base_symbol: string
    method: string
    version: number
  }
  benchmark: {
    b1_low: number
    b2_low: number
    b2_close: number
  }
  results: RsrResultRow[]
}

/**
 * key = `${start}|${end}`
 * value = Map<symbol, RsrResultRow>
 */
type RsrKey = string
type RsrBuffer = Map<RsrKey, Map<string, RsrResultRow>>

function makeKey(start: string, end: string): RsrKey {
  return `${start}|${end}`
}

/* =========================
   Store
========================= */

export const useRsrStore = defineStore("rsr", {
  state: () => ({
    buffer: new Map() as RsrBuffer,

    // 🔥 SINGLE SOURCE OF TRUTH
    activeRange: null as {
      start: string
      end: string
    } | null,

    loading: false,
    error: null as string | null,
  }),

  getters: {
    /* =========================
       Cache check
    ========================= */
    hasForRange: (state) => {
      return (start: string, end: string): boolean => {
        return state.buffer.has(makeKey(start, end))
      }
    },

    /* =========================
       Explicit accessors
    ========================= */
    getRow: (state) => {
      return (
        start: string,
        end: string,
        symbol: string
      ): RsrResultRow | null => {
        return state.buffer.get(makeKey(start, end))?.get(symbol) ?? null
      }
    },

    getRsrClose: (state) => {
      return (
        start: string,
        end: string,
        symbol: string
      ): number | null => {
        return (
          state.buffer
            .get(makeKey(start, end))
            ?.get(symbol)?.rsr_close ?? null
        )
      }
    },

    getRsrLow: (state) => {
      return (
        start: string,
        end: string,
        symbol: string
      ): number | null => {
        return (
          state.buffer
            .get(makeKey(start, end))
            ?.get(symbol)?.rsr_low ?? null
        )
      }
    },
  },

  actions: {
    /* =========================
       Fetch RSR (buffered)
    ========================= */
    async fetch(startDate: string, endDate: string) {
      const key = makeKey(startDate, endDate)

      this.loading = true
      this.error = null

      try {
        // fetch เฉพาะถ้ายังไม่มี
        if (!this.buffer.has(key)) {
          const res: RsrApiResponse = await fetch(
            `https://stock-api.nextbar.trade/rsr?start_date=${startDate}&end_date=${endDate}`
          ).then((r) => r.json())

          const map = new Map<string, RsrResultRow>()
          for (const row of res.results) {
            map.set(row.symbol, row)
          }

          this.buffer.set(key, map)
        }

        // 🔥 สำคัญมาก: set active ทุกครั้ง
        this.activeRange = {
          start: startDate,
          end: endDate,
        }
      } catch (e: any) {
        this.error = e?.message ?? String(e)
      } finally {
        this.loading = false
      }
    },

    /* =========================
       Active value (SORT / UI)
    ========================= */
    getActiveValue(
      symbol: string,
      mode: "close" | "low" = "close"
    ): number | null {
      if (!this.activeRange) return null

      const key = makeKey(
        this.activeRange.start,
        this.activeRange.end
      )

      const row = this.buffer.get(key)?.get(symbol)
      if (!row) return null

      return mode === "low"
        ? row.rsr_low
        : row.rsr_close
    },

    /* =========================
       Reset
    ========================= */
    reset() {
      this.buffer.clear()
      this.activeRange = null
      this.loading = false
      this.error = null
    },
  },
})
